import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLazyQuery } from '@apollo/client';

import { GET_SCHEMA_TREE } from '@atom/graph/schema';
import { Snackbar } from '@atom/mui';
import {
  AIChatAttachment,
  AIChatWorkflows,
  AssetDraftFromPhotoToolOutput,
  DraftAsset,
  DraftElement,
} from '@atom/types/ai';
import { SchemaTree } from '@atom/types/schema';
import api from '@atom/utilities/api';
import {
  mapCreatedElementToDraftElement,
  removeExcludedElements,
  setAttributeValue,
  validateDraftAssetTree,
} from '@atom/utilities/assetDraftUtilities';
import { getWorkflowCompleteEndpoint } from '@atom/utilities/endpoints';
import { getSchemaTreeMap } from '@atom/utilities/schemaUtilities';
import {
  combineSets,
  removeFromSet,
  toggleFromSet,
} from '@atom/utilities/setUtilities';

import { useChatContext } from '../ChatContext';

type ChatAssetDraftContextValue = {
  draftAsset: DraftAsset | null;
  schema: SchemaTree | null;
  disabled: boolean;
  creating: boolean;
  assetId: string | null;
  elementsToShow: DraftElement[];
  expandedPaths: Set<string>;
  excludedPaths: Set<string>;
  errorsByPath: Map<string, Set<string>>;
  handleDraftAssetChange: (patch: Partial<DraftAsset>) => void;
  handleAttributeChange: (
    path: (string | number)[],
    attributeId: string,
    value: unknown,
  ) => void;
  handleToggleExpanded: (pathKey: string) => void;
  handleToggleExcluded: (pathKey: string) => void;
  handleCreate: () => void;
};

const ChatAssetDraftContext = createContext<ChatAssetDraftContextValue | null>(
  null,
);

type ChatAssetDraftProviderProps = {
  toolOutput: AssetDraftFromPhotoToolOutput | undefined;
  toolCallId: string | undefined;
  addResult: ((result: any) => void) | undefined;
  children: ReactNode;
};

export const ChatAssetDraftProvider = ({
  toolOutput,
  toolCallId,
  addResult,
  children,
}: ChatAssetDraftProviderProps) => {
  const result = toolOutput?.result;
  const asset = result?.asset ?? null;
  const isAssetCreated = !!asset;

  const {
    threadId,
    messagesData: { messages },
    attachmentsData: { attachments: attachmentsByMessageId },
  } = useChatContext();

  const threadAttachments = useMemo<AIChatAttachment[]>(() => {
    const fromMetadata = messages.flatMap(
      msg => ((msg as any)?.metadata?.attachments as AIChatAttachment[]) ?? [],
    );

    if (fromMetadata.length > 0) {
      return fromMetadata;
    }

    return Object.values(attachmentsByMessageId).flat();
  }, [messages, attachmentsByMessageId]);

  const [getSchemaTree, { data: schemaTreeData }] = useLazyQuery<{
    schemaTree: SchemaTree;
  }>(GET_SCHEMA_TREE, { fetchPolicy: 'no-cache' });

  const schemaIdToSchema = useMemo(
    () => getSchemaTreeMap(schemaTreeData?.schemaTree),
    [schemaTreeData],
  );

  const [draftAsset, setDraftAsset] = useState<DraftAsset | null>(
    result?.draftAsset ?? null,
  );

  const schema: SchemaTree | null = draftAsset?.schemaId
    ? (schemaIdToSchema.get(draftAsset.schemaId) ?? null)
    : null;

  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const [excludedPaths, setExcludedPaths] = useState<Set<string>>(new Set());
  const [errorsByPath, setErrorsByPath] = useState<Map<string, Set<string>>>(
    new Map(),
  );

  const [creating, setCreating] = useState(false);
  const [assetId, setAssetId] = useState<string | null>(asset?.id ?? null);

  useEffect(() => {
    if (draftAsset?.schemaId) {
      getSchemaTree({ variables: { id: draftAsset.schemaId } });
    }
  }, [draftAsset?.schemaId, getSchemaTree]);

  useEffect(() => {
    if (!isAssetCreated && result?.draftAsset) {
      setDraftAsset(result.draftAsset);
    }
  }, [isAssetCreated, result?.draftAsset]);

  useEffect(() => {
    if (isAssetCreated || !schema?.name || draftAsset?.name) {
      return;
    }
    setDraftAsset(prev => (prev ? { ...prev, name: schema.name } : prev));
  }, [isAssetCreated, schema?.name, draftAsset?.name]);

  const handleDraftAssetChange = useCallback(
    (patch: Partial<DraftAsset>) =>
      setDraftAsset(prev => (prev ? { ...prev, ...patch } : prev)),
    [],
  );

  const handleAttributeChange = useCallback(
    (path: (string | number)[], id: string, value: unknown) => {
      setDraftAsset(prev =>
        prev ? setAttributeValue(prev, path, id, value) : prev,
      );

      const pathKey = path.join('.');
      setErrorsByPath(prev => {
        const current = prev.get(pathKey);

        if (!current?.has(id)) {
          return prev;
        }

        const updated = removeFromSet(current, id);
        const next = new Map(prev);

        if (!updated.size) {
          next.delete(pathKey);
        } else {
          next.set(pathKey, updated);
        }

        return next;
      });
    },
    [],
  );

  const handleToggleExpanded = useCallback((pathKey: string) => {
    setExpandedPaths(prev => toggleFromSet(prev, pathKey));
  }, []);

  const handleToggleExcluded = useCallback((pathKey: string) => {
    setExcludedPaths(prev => toggleFromSet(prev, pathKey));
  }, []);

  const handleCreate = useCallback(async () => {
    if (creating || !draftAsset) {
      return;
    }

    const { errorsByPath: nextErrors, ancestorPathsToExpand } =
      validateDraftAssetTree(draftAsset, schemaIdToSchema, excludedPaths);

    if (nextErrors.size) {
      setErrorsByPath(nextErrors);
      setExpandedPaths(prev => combineSets(prev, ancestorPathsToExpand));
      return;
    }

    setCreating(true);

    try {
      const payload = removeExcludedElements(draftAsset, excludedPaths);

      const attachments = threadAttachments
        .filter(
          attachment =>
            !!attachment.fileId &&
            (!attachment.status || attachment.status === 'ready'),
        )
        .map(attachment => ({
          mediaId: attachment.mediaId,
          mimeType: attachment.mimeType,
          fileId: attachment.fileId,
        }));

      const { data: createdAsset } = await api.post(
        getWorkflowCompleteEndpoint(AIChatWorkflows.ASSET_DRAFT_FROM_PHOTO),
        {
          runId: toolOutput?.runId,
          threadId,
          toolCallId,
          draftAsset: payload,
          attachments,
        },
      );

      if (addResult && toolOutput) {
        addResult({
          ...toolOutput,
          result: { ...toolOutput.result, asset: createdAsset },
        });
      }

      setAssetId(createdAsset?.id ?? null);
      setCreating(false);
    } catch (err) {
      setCreating(false);
      Snackbar.error({ message: 'An error occurred. Please try again.' });
    }
  }, [
    creating,
    draftAsset,
    schemaIdToSchema,
    excludedPaths,
    threadAttachments,
    toolOutput,
    threadId,
    toolCallId,
    addResult,
  ]);

  const createdElementsAsDraft = useMemo(
    () => (asset?.elements ?? []).map(mapCreatedElementToDraftElement),
    [asset?.elements],
  );

  const elementsToShow: DraftElement[] = isAssetCreated
    ? createdElementsAsDraft
    : (draftAsset?.elements ?? []);

  const disabled = isAssetCreated;

  const value = useMemo(
    () => ({
      schema,
      disabled,
      draftAsset,
      creating,
      assetId,
      elementsToShow,
      expandedPaths,
      excludedPaths,
      errorsByPath,
      handleDraftAssetChange,
      handleAttributeChange,
      handleToggleExpanded,
      handleToggleExcluded,
      handleCreate,
    }),
    [
      schema,
      disabled,
      draftAsset,
      creating,
      assetId,
      elementsToShow,
      expandedPaths,
      excludedPaths,
      errorsByPath,
      handleDraftAssetChange,
      handleAttributeChange,
      handleToggleExpanded,
      handleToggleExcluded,
      handleCreate,
    ],
  );

  return (
    <ChatAssetDraftContext.Provider value={value}>
      {children}
    </ChatAssetDraftContext.Provider>
  );
};

export const useChatAssetDraftContext = (): ChatAssetDraftContextValue =>
  useContext(ChatAssetDraftContext);
